import React from 'react'
import { Button, Well } from '@pushly/aqe/lib/components'
import { observer } from 'mobx-react'
import { CloudDownloadOutlined, DollarOutlined, DownloadOutlined, Loading3QuartersOutlined } from '@ant-design/icons'

import { Container } from 'typescript-ioc/es5'
import { ColumnProps } from 'antd/lib/table'
import { titleCase } from '@pushly/models/lib/utils/change-case'
import moment from 'moment-timezone'
// import './style/account-billing-history-well.scss'

import { AccountDto } from '../../dtos/account.dto'
import { AccountService, AppService } from '../../services/index'
import { getClassNames } from '../../_utils/classnames'
import { SUPPORTED_BILLING_CURRENCIES } from '../../constants'
import { numberAsCurrency } from '../../_utils/numeral'

import { IApiResponsePaginationLinks } from '../../interfaces/api-response-pagination-links'
import SwSimpleLinkPagination from '../sw-simple-link-pagination/sw-simple-link-pagination'
import { AntdTableEmptyPlaceholder, CompoundTable } from '@pushly/aqe/lib/components'
import { Flex, Input, Space, Table, Typography } from 'antd'

class RecordTableDataDto {
    public static fromAccountDto(fields: any): RecordTableDataDto {
        const dto = new RecordTableDataDto()
        Object.assign(dto, fields, {
            key: fields.id,
        })
        return dto
    }

    public readonly key: number
}

interface IColumnProps extends ColumnProps<RecordTableDataDto & any> {
    dataKey?: string
}

interface IAccounts {
    loading: boolean
    account: AccountDto
}

interface IAccountsState {
    loading: boolean
    recordsLoaded: boolean
    records: any[]
    recordsMeta: any
    sortedInfo: any
    page: number
    pageSize: number
    query?: string
    paginationLinks?: IApiResponsePaginationLinks
}

@observer
export class AccountBillingHistoryWell extends React.Component<IAccounts, IAccountsState> {
    protected appService: AppService
    protected accountService: AccountService

    public constructor(props: any) {
        super(props)

        this.appService = Container.get(AppService)
        this.accountService = Container.get(AccountService)

        this.state = {
            page: 1,
            pageSize: 15,
            loading: true,
            recordsLoaded: false,
            records: [],
            recordsMeta: {},
            sortedInfo: {
                order: 'ascend',
                columnKey: 'name',
            },
        }
    }

    public componentDidMount(): void {
        this.ensureRecordsForList()
    }

    public async ensureRecordsForList() {
        await this.fetchRecords()
    }

    public render() {
        let { sortedInfo } = this.state
        sortedInfo = sortedInfo || {}

        const buildColumn = (config: IColumnProps): React.ReactNode => {
            const title = (config.title as string) || titleCase(config.dataKey || (config.key as string))
            if (config.sorter) {
                config.sortOrder = sortedInfo.columnKey === (config.dataKey || config.key) && sortedInfo.order
            }

            return (
                <Table.Column
                    {...config}
                    title={title}
                    dataIndex={config.dataKey?.split('.')}
                    key={config.dataKey || config.key}
                    className={config.className || config.dataKey || (config.key as string)}
                />
            )
        }

        return (
            <Table
                title={() => (
                    <Flex justify="space-between">
                        <Space>
                            <Typography.Title level={5}>Billing History</Typography.Title>
                            <Input.Search
                                size="small"
                                className="billing-search"
                                placeholder="Search"
                                enterButton={true}
                                onSearch={this.searchRecords}
                            />
                        </Space>
                        <Button size="small" className="billing-download" onClick={this.downloadRecords}>
                            <CloudDownloadOutlined />
                            &nbsp;
                            <span>Download</span>
                        </Button>
                    </Flex>
                )}
                rowKey={(row) => row.id}
                dataSource={this.state.records}
                onChange={this.handleChange}
                pagination={false}
                footer={(currentTableData) => (
                    <SwSimpleLinkPagination
                        currentTableData={currentTableData}
                        links={this.state.paginationLinks}
                        onPrev={async (_ev, _link, config) => {
                            this.setState({ page: config.current! }, () => this.fetchRecords())
                        }}
                        onNext={async (_ev, _link, config) => {
                            this.setState({ page: config.current! }, () => this.fetchRecords())
                        }}
                    />
                )}
                bordered={true}
                loading={
                    this.isLoading && {
                        indicator: <Loading3QuartersOutlined spin={true} />,
                    }
                }
                locale={{
                    emptyText: <AntdTableEmptyPlaceholder text="No Previous Charges" icon={<DollarOutlined />} />,
                }}
            >
                {buildColumn({
                    dataKey: 'created_at',
                    title: 'Date',
                    render: (_, record) => {
                        return moment.tz(record.created_at, 'UTC').format('LL')
                    },
                })}
                {buildColumn({
                    className: 'amount',
                    dataKey: 'metadata.amount',
                    title: 'Amount',
                    render: (_, record) => {
                        const symbol =
                            SUPPORTED_BILLING_CURRENCIES.find((cur) => cur.value === record.metadata?.currency)
                                ?.symbol ?? '$'
                        return `${symbol}${numberAsCurrency(record.metadata?.amount, 2)}`
                    },
                })}
                {buildColumn({
                    className: 'line_item_description',
                    dataKey: 'metadata.description',
                    title: 'Description',
                })}
                {buildColumn({
                    key: 'actions',
                    title: ' ',
                    render: this.renderRowActions,
                })}
            </Table>
        )
    }

    protected get isLoading(): boolean {
        return this.props.loading || (this.state.loading && !this.state.recordsLoaded)
    }

    protected renderRowActions = (record: any): React.ReactNode => {
        return (
            record.stripe_charge_receipt_url && (
                <Button onClick={() => this.handleReceiptDownloadClick(record)} size="small">
                    <DownloadOutlined />
                    <span>Get Receipt</span>
                </Button>
            )
        )
    }

    protected handleChange = (pagination: any, filters: any, sorter: any) => {
        this.setState({
            sortedInfo: sorter,
        })
    }

    protected handleReceiptDownloadClick(record: any): void {
        window.open(record.stripe_charge_receipt_url)
    }

    protected searchRecords = async (data: string | any) => {
        const value = typeof data !== 'string' ? data.target.value : data

        if (!value || value.length === 0 || value.length >= 2) {
            await this.setState({ page: 1, query: value })
            await this.fetchRecords(value)
        }
    }

    protected downloadRecords = async () => {
        await this.fetchRecords(this.state.query, true)
    }

    protected async fetchRecords(search?: any, csv: boolean = false) {
        if (!this.props.account) {
            setTimeout(() => this.fetchRecords(), 10)
            return
        }

        this.setState({ loading: true, recordsLoaded: false })
        const update: Partial<IAccountsState> = {
            recordsLoaded: true,
        }

        const opts: any = {}
        if (!!search && !!search.toString().trim()) {
            opts.search = search
        }
        if (csv) {
            opts.format = 'csv'
        }
        opts.confirmed = true

        const {
            ok,
            data: records,
            meta,
        } = await this.accountService.fetchBillingRecordsByAccountId(this.props.account.id, opts, false)

        if (!csv && ok) {
            update.records = records
            update.paginationLinks = meta.links
            update.recordsMeta = meta
        }

        return this.setState({
            loading: false,
            ...(update as any),
        })
    }
}
